import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Brain,
  Building2,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  GraduationCap,
  Mail,
  MapPinned,
  Phone,
  ShieldCheck,
  Trophy } from
"lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { InquiryForm } from "@/components/InquiryForm";

const HERO_IMAGE =
"https://images.unsplash.com/photo-1525367922492-f15fe7b709cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBnZW9tZXRyaWMlMjBsaW5lc3xlbnwwfHx8fDE3ODIyOTI2OTZ8MA&ixlib=rb-4.1.0&q=85";
const GEOMETRY_IMAGE =
"https://static.prod-images.emergentagent.com/jobs/52a1a5b6-0066-476c-ab28-fd4027be8e5b/images/28c50d2bea7e90e9d87e273425b64e161ce8b06c9902392142af8fa9e916d9b6.png";
const PHILOSOPHY_IMAGE =
"https://images.unsplash.com/photo-1595494702547-a45e577e2764?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxmb2N1c2VkJTIwc3R1ZGVudCUyMHN0dWR5aW5nJTIwbGlicmFyeSUyMGRhcmt8ZW58MHx8fHwxNzgyMjkyNjk2fDA&ixlib=rb-4.1.0&q=85";
const APPROACH_IMAGE =
"https://images.unsplash.com/photo-1483959651481-dc75b89291f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBnZW9tZXRyaWMlMjBsaW5lc3xlbnwwfHx8fDE3ODIyOTI2OTZ8MA&ixlib=rb-4.1.0&q=85";
const ASCEND_LOGO =
"/ascend-logo.svg";

const navItems = [
{ id: "philosophy", label: "Philosophy" },
{ id: "approach", label: "Approach" },
{ id: "structure", label: "Structure" },
{ id: "subjects", label: "Subjects" },
{ id: "platform", label: "Platform" },
{ id: "results", label: "Results" }];


const subjectChips = [
"VCE Mathematical Methods",
"VCE Specialist Mathematics"];


const subjectPageCards = [
{
  title: "Mathematical Methods",
  route: "/math-methods",
  description:
  "Detailed Unit 1/2 and Unit 3/4 coverage, resources, and services for students aiming to excel in Methods."
},
{
  title: "Specialist Mathematics",
  route: "/specialist-maths",
  description:
  "A dedicated Specialist pathway page covering advanced content strands, support, and performance training."
}];


const heroMetrics = [
{ label: "Two-part weekly system", value: "Online + Remote" },
{ label: "Tutor standard", value: "45+ raw score guidance" },
{ label: "Pricing position", value: "Built to stay affordable" }];


const affordabilityCards = [
{
  title: "Elite support without elite pricing",
  description:
  "Students get the same serious training model found in top Victorian tuition programs, without the traditional weekly price barrier."
},
{
  title: "Made for competitive students on real budgets",
  description:
  "Ascend is designed for students who want to compete with the top of their cohort, but need a price point families can comfortably sustain."
},
{
  title: "Value through structure, not inflated fees",
  description:
  "The academy focuses spending on high-impact teaching, assessment, resources, and feedback instead of charging premium-brand tuition prices."
}];


const comparisonRows = [
{
  label: "45+ raw-score tutor credibility",
  expensive: "Usually highlighted as a premium-only feature",
  ascend: "Included within a more affordable program model"
},
{
  label: "Weekly academic structure",
  expensive: "Often locked behind higher-fee tutoring packages",
  ascend: "Included through content sessions, applied sessions, and exam-period trial support"
},
{
  label: "Testing, feedback, and cohort comparison",
  expensive: "Common in high-fee programs",
  ascend: "Built into the Ascend system without the premium price tag"
},
{
  label: "Resources and exam preparation",
  expensive: "Strong resources, but usually at premium cost",
  ascend: "Practice SACs, exams, homework, walkthroughs, and platform access at accessible pricing"
}];


const pillars = [
{
  title: "Conceptual Understanding",
  description:
  "Students learn why mathematics works, not just what to memorise, so unfamiliar questions become manageable.",
  icon: Brain
},
{
  title: "Exam Execution",
  description:
  "Techniques are trained into repeatable exam habits: method selection, notation, structure, and efficient working.",
  icon: ShieldCheck
},
{
  title: "Performance Under Pressure",
  description:
  "Timed conditions, ranking, and feedback sharpen accuracy, composure, and decision-making when marks matter.",
  icon: Trophy
}];


const approachCards = [
{
  title: "A system, not casual tutoring",
  description:
  "Every session sits inside a deliberate academic sequence: teach with precision, apply immediately, assess rigorously, then refine.",
  icon: Building2,
  testId: "approach-card-system"
},
{
  title: "High-performing cohort culture",
  description:
  "Students are trained within an ambitious cohort that values discipline, accountability, and consistent upward movement.",
  icon: GraduationCap,
  testId: "approach-card-cohort"
},
{
  title: "Continuous reinforcement",
  description:
  "Concepts are revisited through cumulative revision, practice SACs, and weekly assessment cycles so retention stays strong.",
  icon: BookOpen,
  testId: "approach-card-reinforcement"
},
{
  title: "Selective expectations",
  description:
  "Ascend Academy is built for students willing to work seriously, embrace feedback, and pursue an exacting standard.",
  icon: CheckCircle2,
  testId: "approach-card-selective"
}];


const resourceItems = [
{
  title: "Ascend Academy platform",
  description:
  "Students receive access to the Ascend Academy website for exclusive resources, official exam walkthroughs, practice SAC walkthroughs, CAS skills videos, performance guides, worked solutions, and fast support through the Ascend Academy AI."
},
{
  title: "Structured notes",
  description:
  "Clear, ordered notes distil each topic into methods, patterns, and principles students can actually use under pressure."
},
{
  title: "Purpose-built problem sets",
  description:
  "Tasks move beyond routine practice and train students to recognise structure, adapt quickly, and reason precisely."
},
{
  title: "Curated exam-style questions",
  description:
  "Materials reflect VCAA expectations while often exceeding them in complexity, preparing students for the upper end of performance."
},
{
  title: "Cumulative revision",
  description:
  "Older content remains active through ongoing review so students build long-term command, not short-term familiarity."
}];


const assessmentMetrics = [
{
  value: "Weekly tests",
  title: "Timed measurement",
  description: "Students sit regular tests and practice SACs to turn preparation into accountable performance."
},
{
  value: "Cohort ranking",
  title: "Visible benchmarks",
  description: "Ranking shows where a student stands within the group and creates a healthy standard of competitive focus."
},
{
  value: "Detailed feedback",
  title: "Precision refinement",
  description: "Strengths, weaknesses, and recurring errors are identified clearly so effort is directed where it matters most."
},
{
  value: "Resilience training",
  title: "Pressure with purpose",
  description: "Competition is used to build calm, discipline, and academic resilience rather than empty intensity."
}];


const platformFeatures = [
"Exclusive subject resources organised for Mathematical Methods and Specialist Mathematics.",
"Official exam walkthroughs, practice SAC walkthroughs, CAS skills videos, and performance guides.",
"Worked solutions, practice SACs, and VCAA exam material in one student platform.",
"Ascend Academy AI for immediate explanations when students need fast guidance."];


const contactDetails = {
  email: "enquiry.ascendacademy@gmail.com",
  phone: "+61 410 243 833"
};

const sectionTitle = (eyebrow, title, description, testId) =>
<div className="max-w-3xl space-y-5">
    <p className="eyebrow text-[#002147]/70" data-testid={`${testId}-eyebrow`}>
      {eyebrow}
    </p>
    <h2
    className="font-display text-4xl tracking-tight text-[#002147] md:text-5xl"
    data-testid={`${testId}-heading`}>

      {title}
    </h2>
    <p className="max-w-2xl text-sm leading-7 text-slate-700 md:text-base" data-testid={`${testId}-description`}>
      {description}
    </p>
  </div>;


export const AscendLandingPage = () => {
  return (
    <div className="ascend-shell bg-background text-[#10233d]" data-testid="ascend-homepage">
      <header
        className="sticky top-0 z-50 border-b border-[#002147]/10 bg-[#f9f6f0]/85 backdrop-blur-xl"
        data-testid="main-navigation">

        <div className="ascend-container flex flex-wrap items-center justify-between gap-4 py-4">
          <a
            href="#top"
            className="font-display text-2xl font-semibold tracking-tight text-[#002147]"
            data-testid="nav-logo">

            Ascend Academy
          </a>
          <nav className="hidden items-center gap-6 md:flex" data-testid="nav-links">
            {navItems.map((item) =>
            <a
              key={item.id}
              href={`#${item.id}`}
              className="text-sm font-medium text-[#173458] transition-colors duration-300 hover:text-[#002147]"
              data-testid={`nav-link-${item.id}`}>

                {item.label}
              </a>
            )}
          </nav>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" data-testid="nav-platform-button">
              <Link to="/platform">Student Login</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" data-testid="nav-admin-button">
              <Link to="/platform?mode=admin">Admin Login</Link>
            </Button>
            <Button asChild className="rounded-sm bg-[#002147] px-5 text-white hover:bg-[#001530]" data-testid="nav-cta-button">
              <a href="#inquiry">Free Info Session</a>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section id="top" className="relative overflow-hidden border-b border-[#002147]/10" data-testid="hero-section">
          <div className="absolute inset-0">
            <img
              src={HERO_IMAGE}
              alt="Ascend Academy learning environment"
              className="h-full w-full object-cover object-center"
              data-testid="hero-background-image" />

            <div className="hero-overlay absolute inset-0" />
          </div>

          <div className="ascend-container relative grid min-h-[calc(100vh-76px)] items-center gap-12 py-16 lg:grid-cols-[1.25fr_0.75fr] lg:py-24">
            <div className="space-y-8 text-white rise">
              <p className="eyebrow text-white/75" data-testid="hero-overline">
                Prestige • Clarity • Purpose
              </p>
              <div className="space-y-6">
                <div className="w-full max-w-[220px]" data-testid="hero-logo-block">
                  <img
                    src={ASCEND_LOGO}
                    alt="Ascend Academy crest logo"
                    className="mx-auto h-auto w-full object-contain"
                    data-testid="hero-logo-image" />
                </div>
                <h1
                  className="font-display max-w-4xl text-4xl leading-none tracking-tight sm:text-5xl lg:text-6xl"
                  data-testid="hero-heading">

                  Top-tier VCE maths tuition without the traditional tuition price tag.
                </h1>
                <p className="max-w-3xl text-sm leading-7 text-white/88 md:text-base" data-testid="hero-subheading">
                  Ascend Academy gives VCE Mathematical Methods and Specialist Mathematics students access to the same serious structure,
                  competitive standards, and exam-focused preparation offered by Victoria’s strongest tuition companies — at pricing that
                  budget-conscious students and families can realistically sustain.
                </p>
                <div className="inline-flex max-w-2xl items-center gap-3 border border-white/15 bg-white/5 px-4 py-3 text-sm text-white/88 backdrop-blur-sm" data-testid="hero-credibility-strip">
                  <Trophy className="h-4 w-4 text-[#d4af37]" />
                  <span>Learn from a 45+ raw study score tutor while paying far less than traditional premium tuition services.</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3" data-testid="hero-subject-chips">
                {subjectChips.map((chip) =>
                <span
                  key={chip}
                  className="rounded-sm border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/92 backdrop-blur-sm"
                  data-testid={`subject-chip-${chip.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>

                    {chip}
                  </span>
                )}
                <span
                  className="rounded-sm border border-[#c5a059]/40 bg-[#c5a059]/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#f6dfb3]"
                  data-testid="subject-chip-future">

                  More subjects on the way
                </span>
              </div>

              <div className="flex flex-wrap gap-4">
                <Button
                  asChild
                  className="rounded-sm bg-[#c5a059] px-6 text-[#002147] hover:bg-[#b48d4d]"
                  data-testid="hero-cta-button">

                  <a href="#inquiry">
                    Enquire About a Free Information Session
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-sm border-white/30 bg-white/5 px-6 text-white hover:bg-white/15 hover:text-white"
                  data-testid="hero-secondary-button">

                  <a href="#structure">Explore the Program</a>
                </Button>
              </div>
            </div>

            <Card className="section-frame rise delay-2 rounded-sm border-[#ffffff24] bg-white/10 text-white shadow-none backdrop-blur-sm" data-testid="hero-summary-card">
              <CardContent className="space-y-8 p-8">
                <div className="space-y-3">
                  <p className="eyebrow text-white/65" data-testid="hero-card-overline">
                    Why families choose Ascend
                  </p>
                  <h2 className="font-display text-3xl tracking-tight" data-testid="hero-card-heading">
                    Premium-quality tutoring without premium pricing.
                  </h2>
                </div>
                <div className="grid gap-4">
                  {heroMetrics.map((metric) =>
                  <div
                    key={metric.label}
                    className="border border-white/15 bg-white/5 p-4"
                    data-testid={`hero-metric-${metric.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>

                      <p className="text-xs uppercase tracking-[0.18em] text-white/60">{metric.label}</p>
                      <p className="mt-2 text-lg font-semibold text-white">{metric.value}</p>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between border-t border-white/15 pt-5 text-sm text-white/75" data-testid="hero-card-footer">
                  <span>Weekday content</span>
                  <ChevronRight className="h-4 w-4" />
                  <span>Weekend application</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="border-y border-white/10 bg-black/80 py-5" data-testid="credibility-banner">
          <div className="ascend-container flex flex-col gap-3 text-center lg:flex-row lg:items-center lg:justify-between lg:text-left">
            <p className="font-display text-2xl tracking-tight text-white" data-testid="credibility-banner-heading">
              Serious VCE Methods and Specialist tuition for students who want elite preparation on an affordable budget.
            </p>
            <p className="text-sm uppercase tracking-[0.18em] text-[#d4af37]" data-testid="credibility-banner-copy">
              45+ raw score credibility • accessible pricing • premium academic structure
            </p>
          </div>
        </section>

        <section className="ascend-section" data-testid="problem-agitation-section">
          <div className="ascend-container space-y-12">
            {sectionTitle(
              "Why Ascend Exists",
              "You should not have to choose between high-quality maths tuition and pricing your family can actually afford.",
              "Too many students know they need stronger structure, tougher assessment practice, and better feedback — but the strongest tuition companies often price that support like a luxury service. Ascend Academy is built to close that gap.",
              "problem-agitation-section"
            )}

            <div className="grid gap-5 lg:grid-cols-3">
              {affordabilityCards.map((card) =>
              <Card key={card.title} className="section-frame rounded-sm bg-white shadow-none" data-testid={`affordability-card-${card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <CardContent className="space-y-4 p-7">
                    <div className="h-px w-16 bg-[#d4af37]" />
                    <h3 className="font-display text-3xl text-white" data-testid={`affordability-card-title-${card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {card.title}
                    </h3>
                    <p className="text-sm leading-7 text-[#d4d4d8]" data-testid={`affordability-card-description-${card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {card.description}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>

        <section className="ascend-section bg-[#06080d]" data-testid="pricing-comparison-section">
          <div className="ascend-container space-y-12">
            {sectionTitle(
              "Direct Comparison",
              "What expensive tuition companies offer — and how Ascend delivers that same serious standard more affordably.",
              "Ascend Academy is not trying to be a cheaper version of weak tutoring. It is built to deliver high-level structure, strong accountability, and serious exam preparation while staying accessible to students who cannot justify inflated tuition fees.",
              "pricing-comparison-section"
            )}

            <div className="overflow-hidden rounded-[1.15rem] border border-white/10" data-testid="pricing-comparison-table">
              <div className="grid grid-cols-1 border-b border-white/10 bg-white/[0.03] text-sm font-semibold text-white md:grid-cols-[0.9fr_1fr_1fr]">
                <div className="border-b border-white/10 px-6 py-5 md:border-b-0 md:border-r md:border-white/10">What matters</div>
                <div className="border-b border-white/10 px-6 py-5 text-[#d4d4d8] md:border-b-0 md:border-r md:border-white/10">Expensive tuition companies</div>
                <div className="px-6 py-5 text-[#d4af37]">Ascend Academy</div>
              </div>
              {comparisonRows.map((row) =>
              <div key={row.label} className="grid grid-cols-1 border-b border-white/10 text-sm md:grid-cols-[0.9fr_1fr_1fr]" data-testid={`comparison-row-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="border-b border-white/10 px-6 py-5 font-medium text-white md:border-b-0 md:border-r md:border-white/10">{row.label}</div>
                  <div className="border-b border-white/10 px-6 py-5 text-[#d4d4d8] md:border-b-0 md:border-r md:border-white/10">{row.expensive}</div>
                  <div className="px-6 py-5 text-[#f4f4f5]">{row.ascend}</div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section id="philosophy" className="ascend-section paper-grid" data-testid="philosophy-section">
          <div className="ascend-container grid gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div className="space-y-8">
              {sectionTitle(
                "Our Philosophy and Values",
                "Mathematical excellence is trained through structure, discipline, and exam mastery.",
                "Ascend Academy is built on the conviction that high VCE mathematics results should not be reserved only for families who can afford the most expensive tuition brands. They are developed through deliberate systems, consistent standards, and the willingness to perform at a higher level every week.",
                "philosophy-section"
              )}
              <div className="section-frame overflow-hidden rounded-sm bg-white" data-testid="philosophy-image-card">
                <img
                  src={PHILOSOPHY_IMAGE}
                  alt="Students studying in a traditional academic setting"
                  className="aspect-[4/3] w-full object-cover object-center"
                  data-testid="philosophy-image" />

              </div>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {pillars.map((pillar, index) => {
                const Icon = pillar.icon;

                return (
                  <Card key={pillar.title} className="section-frame rounded-sm bg-white shadow-none" data-testid={`philosophy-pillar-${index + 1}`}>
                    <CardContent className="space-y-5 p-6">
                      <div className="flex h-12 w-12 items-center justify-center border border-[#002147]/12 bg-[#f4efe3] text-[#002147]" data-testid={`philosophy-pillar-icon-${index + 1}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="space-y-3">
                        <h3 className="font-display text-2xl !text-[#FFFFFF]" data-testid={`philosophy-pillar-title-${index + 1}`}>
                          {pillar.title}
                        </h3>
                        <p className="text-sm leading-7 !text-[#C2C5C9]" data-testid={`philosophy-pillar-copy-${index + 1}`}>
                          {pillar.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>);

              })}

              <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none md:col-span-3" data-testid="philosophy-values-card">
                <CardContent className="grid gap-6 p-6 md:grid-cols-[1.2fr_0.8fr] md:items-center">
                  <div className="space-y-3">
                    <p className="eyebrow text-[#f3d8a1]" data-testid="philosophy-values-overline">
                      The Ascend standard
                    </p>
                    <p className="text-sm leading-7 text-white/86 md:text-base" data-testid="philosophy-values-copy">
                      Academic excellence, consistency, accountability, and ambition sit at the centre of the Ascend culture.
                      The academy is not designed for passive attendance. It is built for students prepared to commit to a serious standard.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3" data-testid="philosophy-values-list">
                    {["Academic excellence", "Consistency", "Accountability", "Ambition"].map((value) =>
                    <span key={value} className="border border-white/20 px-3 py-2 text-xs uppercase tracking-[0.18em] text-white/90" data-testid={`philosophy-value-${value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {value}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section id="approach" className="ascend-section bg-[#f4efe5]" data-testid="approach-section">
          <div className="ascend-container space-y-12">
            {sectionTitle(
              "Our Approach",
              "Ascend Academy operates as a performance system rather than a tutoring service.",
              "Students are taught with precision, then trained to execute under exam conditions. Every element of the program is intentional: deep conceptual teaching, immediate application, continual testing, and actionable feedback inside a high-performing academic cohort.",
              "approach-section"
            )}

            <div className="grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="grid gap-5 md:grid-cols-2">
                {approachCards.map((card) => {
                  const Icon = card.icon;

                  return (
                    <Card key={card.title} className="section-frame rounded-sm bg-white shadow-none" data-testid={card.testId}>
                      <CardContent className="space-y-4 p-6">
                        <div className="flex h-11 w-11 items-center justify-center border border-[#002147]/12 bg-[#f9f6f0] text-[#002147]" data-testid={`${card.testId}-icon`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <h3 className="font-display text-2xl text-[#002147]" data-testid={`${card.testId}-title`}>
                          {card.title}
                        </h3>
                        <p className="text-sm leading-7 text-slate-700" data-testid={`${card.testId}-description`}>
                          {card.description}
                        </p>
                      </CardContent>
                    </Card>);

                })}
              </div>

              <Card className="section-frame overflow-hidden rounded-sm bg-white shadow-none" data-testid="approach-visual-card">
                <div className="grid h-full gap-0 lg:grid-rows-[1fr_auto]">
                  <img
                    src={APPROACH_IMAGE}
                    alt="Traditional university architecture"
                    className="aspect-[4/3] w-full object-cover object-center"
                    data-testid="approach-image" />

                  <div className="space-y-4 p-6">
                    <p className="eyebrow text-[#002147]/60" data-testid="approach-visual-overline">
                      Precision in design
                    </p>
                    <p className="text-sm leading-7 text-slate-700" data-testid="approach-visual-copy">
                      The structure mirrors the standards students are expected to meet: clear sequencing, exact expectations, and repeated exposure to demanding work.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        <section id="structure" className="ascend-section" data-testid="program-structure-section">
          <div className="ascend-container space-y-12">
            {sectionTitle(
              "Program Structure",
              "Two sessions each week. One builds knowledge; the other trains performance.",
              "Ascend Academy’s weekly model separates understanding from execution so both can be developed properly. Students first master the material, then apply it under conditions that resemble SACs and exams.",
              "program-structure-section"
            )}

            <div className="grid gap-6 lg:grid-cols-3">
              <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="content-session-card">
                <CardContent className="space-y-6 p-8">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center border border-[#002147]/12 bg-[#f4efe3] text-[#002147]" data-testid="content-session-icon">
                      <CalendarClock className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="eyebrow text-[#002147]/55" data-testid="content-session-overline">Weekday session</p>
                      <h3 className="font-display text-3xl text-[#002147]" data-testid="content-session-title">Content Session · 2 hours · Online</h3>
                    </div>
                  </div>
                  <p className="text-sm leading-7 text-slate-700 md:text-base" data-testid="content-session-description">
                    New material is introduced in a focused academic setting, broken down with clarity, and developed through structured teaching, worked examples, and guided practice.
                    Students finish by attempting exam-style questions independently so understanding is tested immediately.
                  </p>
                  <ul className="grid gap-3 text-sm text-slate-700" data-testid="content-session-points">
                    {[
                    "Deep conceptual explanation and method selection",
                    "Worked examples with explicit mathematical reasoning",
                    "Guided practice that transitions into independent execution"].
                    map((item) =>
                    <li key={item} className="flex gap-3" data-testid={`content-session-point-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#c5a059]" />
                        <span>{item}</span>
                      </li>
                    )}
                  </ul>
                </CardContent>
              </Card>

              <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="applied-session-card">
                <CardContent className="space-y-6 p-8">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center border border-white/15 bg-white/10 text-white" data-testid="applied-session-icon">
                      <BarChart3 className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="eyebrow text-[#f3d8a1]" data-testid="applied-session-overline">Weekend session</p>
                      <h3 className="font-display text-3xl text-white" data-testid="applied-session-title">Applied Session · 1–1.5 hours · Remote</h3>
                    </div>
                  </div>
                  <p className="text-sm leading-7 text-white/85 md:text-base" data-testid="applied-session-description">
                    This is the performance training arm of the academy. Students engage with SAC-style and exam-style questions, then complete a timed assessment based on the week’s material alongside cumulative revision.
                    It is where speed, accuracy, and exam composure are deliberately developed.
                  </p>
                  <ul className="grid gap-3 text-sm text-white/85" data-testid="applied-session-points">
                    {[
                    "Exam simulation under timed conditions",
                    "Cumulative revision to retain earlier topics",
                    "Method refinement through feedback and correction"].
                    map((item) =>
                    <li key={item} className="flex gap-3" data-testid={`applied-session-point-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#f3d8a1]" />
                        <span>{item}</span>
                      </li>
                    )}
                  </ul>
                </CardContent>
              </Card>

              <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="trial-exam-card">
                <CardContent className="space-y-6 p-8">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center border border-[#002147]/12 bg-[#f4efe3] text-[#002147]" data-testid="trial-exam-icon">
                      <Trophy className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="eyebrow text-[#002147]/55" data-testid="trial-exam-overline">Exam-period structure</p>
                      <h3 className="font-display text-3xl text-[#002147]" data-testid="trial-exam-title">Live Trial Exams · In person</h3>
                    </div>
                  </div>
                  <p className="text-sm leading-7 text-slate-700 md:text-base" data-testid="trial-exam-description">
                    Near the exam period, Ascend Academy runs live in-person trial exams as part of the broader structure. These sessions are designed to simulate serious exam conditions and are followed by in-depth marking, score projections, and targeted feedback.
                  </p>
                  <ul className="grid gap-3 text-sm text-slate-700" data-testid="trial-exam-points">
                    {[
                    "Live in-person trial papers completed under exam conditions",
                    "Detailed marking to identify strengths, weaknesses, and mark leakage",
                    "Performance projections and feedback to sharpen final preparation"].
                    map((item) =>
                    <li key={item} className="flex gap-3" data-testid={`trial-exam-point-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#c5a059]" />
                        <span>{item}</span>
                      </li>
                    )}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section id="subjects" className="ascend-section bg-[#f4efe5]" data-testid="subjects-section">
          <div className="ascend-container space-y-12">
            {sectionTitle(
              "Subject-Specific Programs",
              "Dedicated program pages for Mathematical Methods and Specialist Mathematics.",
              "Each subject page outlines the unit coverage, the resources students receive, and the services that support strong SAC and exam performance across the VCE year.",
              "subjects-section"
            )}

            <div className="grid gap-6 lg:grid-cols-2">
              {subjectPageCards.map((subject) =>
              <Card key={subject.title} className="section-frame rounded-sm bg-white shadow-none" data-testid={`subject-page-card-${subject.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <CardContent className="space-y-5 p-8">
                    <p className="eyebrow text-[#002147]/60" data-testid={`subject-page-card-overline-${subject.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      Subject pathway
                    </p>
                    <h3 className="font-display text-3xl !text-[#FFFFFF]" data-testid={`subject-page-card-title-${subject.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {subject.title}
                    </h3>
                    <p className="text-sm leading-7 !text-[#C6C8C8]" data-testid={`subject-page-card-description-${subject.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {subject.description}
                    </p>
                    <Button asChild className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" data-testid={`subject-page-card-button-${subject.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <Link to={subject.route}>
                        View Subject Page
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>

        <section id="platform" className="ascend-section" data-testid="platform-section">
          <div className="ascend-container grid gap-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-stretch">
            <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="platform-feature-card">
              <CardContent className="space-y-6 p-8 md:p-10">
                <div className="space-y-4">
                  <p className="eyebrow text-[#f3d8a1]" data-testid="platform-overline">Ascend Academy Platform</p>
                  <h2 className="font-display text-4xl tracking-tight text-white md:text-5xl" data-testid="platform-heading">
                    A dedicated student website for resources, revision, and immediate support.
                  </h2>
                  <p className="max-w-2xl text-sm leading-7 text-white/82 md:text-base" data-testid="platform-description">
                    Students receive access to the Ascend Academy Platform as part of the program. It combines weekly resources, submission workflows, official walkthrough videos, performance guides, and fast support so students can revise efficiently and get unstuck without delay.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button asChild className="rounded-sm bg-[#c5a059] text-[#002147] hover:bg-[#b48d4d]" data-testid="platform-cta-button">
                    <Link to="/platform">
                      Student Login
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-sm border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white" data-testid="platform-enquiry-button">
                    <a href="#inquiry">Enquire About Access</a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="platform-benefits-card">
              <CardContent className="space-y-5 p-8 md:p-10">
                <p className="eyebrow text-[#002147]/60" data-testid="platform-benefits-overline">What the platform includes</p>
                <div className="grid gap-4" data-testid="platform-benefits-list">
                  {platformFeatures.map((feature) =>
                  <div key={feature} className="flex border border-[#002147]/10 gap-3 p-4 !bg-[#858585]" data-testid={`platform-feature-${feature.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#c5a059]" />
                      <p className="text-sm leading-7 !text-[#000000]">{feature}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section
          className="ascend-section relative overflow-hidden bg-[#001a39] text-white dark-grid"
          data-testid="assessment-section">

          <div className="absolute inset-0 opacity-20">
            <img src={GEOMETRY_IMAGE} alt="Geometric mathematics texture" className="h-full w-full object-cover object-center" data-testid="assessment-background-image" />
          </div>
          <div className="ascend-container relative space-y-12">
            {sectionTitle(
              "Assessment and Performance Tracking",
              "Measured performance creates clarity, accountability, and steady upward pressure.",
              "Students complete weekly tests and practice SACs under timed conditions. Performance is tracked systematically so every student knows where they are strong, where they are losing marks, and how they compare within the cohort. Ranking is used to build resilience, focus, and disciplined ambition.",
              "assessment-section"
            )}

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              {assessmentMetrics.map((metric) =>
              <Card key={metric.title} className="border border-white/12 bg-white/6 rounded-sm text-white shadow-none backdrop-blur-sm" data-testid={`tracking-metric-card-${metric.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <CardContent className="space-y-4 p-6">
                    <p className="text-xs uppercase tracking-[0.18em] text-[#f3d8a1]" data-testid={`tracking-metric-value-${metric.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {metric.value}
                    </p>
                    <h3 className="font-display text-2xl text-white" data-testid={`tracking-metric-title-${metric.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {metric.title}
                    </h3>
                    <p className="text-sm leading-7 text-white/78" data-testid={`tracking-metric-description-${metric.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {metric.description}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>

        <section className="ascend-section" data-testid="materials-section">
          <div className="ascend-container space-y-12">
            {sectionTitle(
              "Materials and Resources",
              "Every resource is built to deepen understanding and improve adaptability.",
              "Ascend Academy materials are not designed as generic worksheets. They are constructed to sharpen reasoning, expose students to high-value patterns, and strengthen long-term retention across the full VCE year.",
              "materials-section"
            )}

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              {resourceItems.map((item) =>
              <Card key={item.title} className="section-frame rounded-sm bg-white shadow-none" data-testid={`resource-item-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <CardContent className="space-y-4 p-6">
                    <div className="h-px w-12 bg-[#c5a059]" data-testid={`resource-divider-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                    <h3 className="font-display text-2xl !text-[#B6B6B6]" data-testid={`resource-title-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {item.title}
                    </h3>
                    <p className="text-sm leading-7 !text-[#E1DBDB]" data-testid={`resource-description-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>

        <section id="results" className="ascend-section bg-[#f4efe5]" data-testid="results-section">
          <div className="ascend-container space-y-10 text-center">
            <p className="eyebrow text-[#002147]/65" data-testid="results-overline">Results and Outcomes</p>
            <h2 className="font-display mx-auto max-w-4xl text-4xl tracking-tight text-[#002147] md:text-5xl" data-testid="results-heading">
              Built to maximise each student’s academic potential in VCE mathematics.
            </h2>
            <p className="mx-auto max-w-3xl text-sm leading-7 text-slate-700 md:text-base" data-testid="results-description">
              Ascend Academy is designed for students targeting strong 40+ outcomes and, for the most ambitious, 45+ study scores.
              Through rigorous teaching, consistent assessment, disciplined feedback, and a high-standard cohort environment, the academy creates the conditions for exceptional progress without the inflated fees that often lock students out of premium tuition.
            </p>

            <div className="grid gap-5 md:grid-cols-3">
              {[
              {
                title: "Ambitious standards",
                description: "Students are taught to pursue excellence with seriousness, not vague motivation."
              },
              {
                title: "Consistent measurement",
                description: "Weekly testing ensures progress is visible, accountable, and continuously refined."
              },
              {
                title: "Long-term credibility",
                description: "This section is ready to expand with future testimonials, study scores, and success stories as cohorts mature."
              }].
              map((item) =>
              <Card key={item.title} className="section-frame rounded-sm bg-white shadow-none" data-testid={`results-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <CardContent className="space-y-3 p-6 text-left">
                    <h3 className="font-display text-2xl !text-[#E7E7E8]" data-testid={`results-card-title-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {item.title}
                    </h3>
                    <p className="text-sm leading-7 !text-[#F4F2F2]" data-testid={`results-card-description-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>

        <section id="inquiry" className="ascend-section" data-testid="inquiry-section">
          <div className="ascend-container grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="inquiry-details-card">
              <CardContent className="space-y-7 p-8">
                <div className="space-y-4">
                  <p className="eyebrow text-[#f3d8a1]" data-testid="inquiry-details-overline">Free Information Session & Trial</p>
                  <h2 className="font-display text-4xl tracking-tight text-white" data-testid="inquiry-details-heading">
                    Learn how the Ascend system works before committing.
                  </h2>
                  <p className="text-sm leading-7 text-white/84 md:text-base" data-testid="inquiry-details-copy">
                    Prospective students and parents can register interest for a free consultation where the program, expectations, and weekly structure are explained clearly.
                    From there, suitable students may be invited to a free trial class to experience both the teaching style and the academic standard — and to see how Ascend delivers premium-level support at a far more affordable price point.
                  </p>
                </div>

                <div className="grid gap-4" data-testid="inquiry-steps-list">
                  {[
                  "Submit an enquiry with current subjects and academic goals.",
                  "Receive a follow-up to arrange a free information session.",
                  "Explore a trial class if the program is the right fit."].
                  map((step) =>
                  <div key={step} className="flex gap-3 border border-white/12 bg-white/5 p-4" data-testid={`inquiry-step-${step.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#f3d8a1]" />
                      <p className="text-sm text-white/84">{step}</p>
                    </div>
                  )}
                </div>

                <div className="grid gap-4 border-t border-white/12 pt-6" data-testid="footer-contact">
                  <a href={`mailto:${contactDetails.email}`} className="flex items-center gap-3 text-sm text-white/90 transition-colors duration-300 hover:text-white" data-testid="footer-email">
                    <Mail className="h-4 w-4 text-[#f3d8a1]" />
                    {contactDetails.email}
                  </a>
                  <a href={`tel:${contactDetails.phone.replace(/\s+/g, "")}`} className="flex items-center gap-3 text-sm text-white/90 transition-colors duration-300 hover:text-white" data-testid="footer-phone">
                    <Phone className="h-4 w-4 text-[#f3d8a1]" />
                    {contactDetails.phone}
                  </a>
                  <div className="flex items-center gap-3 text-sm text-white/80" data-testid="inquiry-location-note">
                    <MapPinned className="h-4 w-4 text-[#f3d8a1]" />
                    Weekend applied sessions are delivered remotely, with live in-person trial exams introduced near the exam period.
                  </div>
                </div>
              </CardContent>
            </Card>

            <InquiryForm contactDetails={contactDetails} />
          </div>
        </section>
      </main>

      <footer className="border-t border-[#c5a059]/15 bg-[#002147] py-10 text-white" data-testid="footer-section">
        <div className="ascend-container flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-3">
            <div className="w-full max-w-[160px]" data-testid="footer-logo-block">
              <img
                src={ASCEND_LOGO}
                alt="Ascend Academy footer logo"
                className="mx-auto h-auto w-full object-contain"
                data-testid="footer-logo-image" />
            </div>
            <p className="font-display text-3xl tracking-tight text-white" data-testid="footer-heading">
              Ascend Academy
            </p>
            <p className="max-w-2xl text-sm leading-7 text-white/75" data-testid="footer-message">
              A specialised academic institution for high-performing VCE mathematics students — combining rigorous instruction, performance conditioning, and disciplined standards.
            </p>
          </div>
          <div className="space-y-2 text-sm text-white/80 md:text-right" data-testid="footer-contact-block">
            <p data-testid="footer-contact-email">{contactDetails.email}</p>
            <p data-testid="footer-contact-phone">{contactDetails.phone}</p>
            <p data-testid="footer-copyright">© {new Date().getFullYear()} Ascend Academy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>);

};